/**
 * What a movement's box draws when the catalogue holds no photograph of it.
 *
 * The canon never leaves the box empty: every mock that draws an exercise still draws a tile in the
 * movement's own muscle hue carrying one of these two shapes — a barbell for work, a heartbeat for
 * preparation. The surrounding tile is the CALLER's, because its geometry and its tint differ per
 * surface (40×30 at 14% in the week card, 48×36 at 13% inside a hairline in the library drawer);
 * what does not differ is the drawing, and this file is it.
 *
 * ── Why it is `shared/` ─────────────────────────────────────────────────────────────────────
 * The barbell reached its second real consumer on 9 September 2026: `pages/plan-builder`'s week card
 * drew it first, and `features/exercise-library-rail` now draws it on every row whose movement has
 * no still — which, on the local media root, is most of them. A feature may not import from a page,
 * so the drawing had to come here or be transcribed twice, and a path string transcribed twice is
 * exactly the near-duplicate the house rules exist to prevent.
 *
 * The heartbeat still has ONE consumer and ships beside it deliberately: the two are one pair in one
 * mock, chosen by a single boolean at the only place either is drawn, and splitting them would put
 * the `<svg>` construction in two files to keep one path string in one. If the pair ever stops being
 * a pair, that is the moment to split it.
 *
 * Decorative in every use so far — the movement's name stands beside the tile, so a described glyph
 * would make a screen reader read the exercise twice. `aria-hidden` is therefore baked in rather
 * than offered as a prop; a caller that needs an announced version needs a different component.
 */

interface GlyphProps {
  className?: string;
  /**
   * The stroke's weight, in the 24-box's own units.
   *
   * A prop rather than a constant because the mock varies it with the drawn SIZE and not with the
   * shape — 2 at 13 and 15px, 1.9 at 20px, 1.6 at 38px — which is optical compensation, not a
   * second drawing. Two call sites, two values, so it is a real seam rather than a hypothetical one.
   */
  strokeWidth?: number;
}

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  'aria-hidden': true,
} as const;

/** Work: a bar with a plate at each end. */
export function BarbellGlyph({ className, strokeWidth = 2 }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS} strokeWidth={strokeWidth}>
      <path d="M4 9v6M7.5 6.5v11M7.5 12h9M16.5 6.5v11M20 9v6" />
    </svg>
  );
}

/** Preparation: a heartbeat, which is what a warm-up row raises. */
export function WarmupGlyph({ className, strokeWidth = 2 }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS} strokeWidth={strokeWidth}>
      <path d="M3 12h3.4l1.7-5 3.3 11 2-6 1.3 3H21" />
    </svg>
  );
}
